import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { AttendeesTable } from "./AttendeesTable";
import { DesignerPanel } from "./DesignerPanel";
import { BulkPanel } from "./BulkPanel";
import { BadgeColorLegend } from "./BadgeColorLegend";
import { useAttendees } from "./useAttendees";
import { useConferenceBranding } from "./useConferenceBranding";
import type { AttendeeType } from "./colors";

interface Props {
  userRole: string;
  userRegionId: string | null | undefined;
}

const normalizeRegion = (v?: string | null) =>
  (v || "")
    .toLowerCase()
    .replace(/\bregion\b/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export default function BadgeGenerator({ userRole, userRegionId }: Props) {
  const [regionName, setRegionName] = useState<string | null>(null);

  // Any admin tied to a region (regional / KIT) is locked to that region's roster
  // and that region's conference details. Global admin & president see all regions.
  const isRegionLocked = userRole !== "global_admin" && userRole !== "president";

  useEffect(() => {
    (async () => {
      if (isRegionLocked && userRegionId) {
        const { data } = await supabase.from("regions").select("name").eq("id", userRegionId).maybeSingle();
        setRegionName((data as any)?.name || null);
      } else {
        setRegionName(null);
      }
    })();
  }, [isRegionLocked, userRegionId]);

  const { attendees, setAttendees, regions, loading, refreshing, error, refresh } = useAttendees(regionName);

  const [pickedRegion, setPickedRegion] = useState<string>("all");
  const activeRegion = regionName || (pickedRegion !== "all" ? pickedRegion : null);
  const brandingRegion = activeRegion || "All Regions";
  // Conference details (title, theme, venue, dates) always follow the active region.
  const { branding } = useConferenceBranding(activeRegion || "");
  const needsRegionPick = !activeRegion;

  const visibleAttendees = useMemo(
    () => (activeRegion
      ? attendees.filter((a) => normalizeRegion(a.region) === normalizeRegion(activeRegion))
      : attendees),
    [attendees, activeRegion],
  );


  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState<string>("letter-portrait-5mm");
  const [tab, setTab] = useState<string>("attendees");

  useEffect(() => {
    if (!activeKey && visibleAttendees.length) setActiveKey(visibleAttendees[0].key);
  }, [visibleAttendees, activeKey]);

  const selectedAttendees = useMemo(
    () => visibleAttendees.filter((a) => selectedKeys.has(a.key)),
    [visibleAttendees, selectedKeys],
  );


  const onOverrideType = (key: string, type: AttendeeType) => {
    setAttendees((prev) => prev.map((a) => (a.key === key ? { ...a, attendeeType: type } : a)));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          <span className="ml-3 text-muted-foreground">Loading attendees…</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-destructive">Failed to load: {error}</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle>Badge Generator · {brandingRegion}</CardTitle>
          <div className="flex items-center gap-2">
            {!isRegionLocked && (
              <Select value={pickedRegion} onValueChange={setPickedRegion}>
                <SelectTrigger className="w-[190px] h-9">
                  <SelectValue placeholder="All regions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All regions</SelectItem>
                  {regions.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={refresh}
              disabled={refreshing}
              aria-label="Refresh badges"
            >
              {refreshing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              {refreshing ? "Refreshing…" : "Refresh badges"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-1">
          <div>
            {visibleAttendees.length} attendees{activeRegion ? ` in ${activeRegion}` : ` across ${regions.length || 1} region${(regions.length || 1) === 1 ? "" : "s"}`} for the current conference.
          </div>
          {activeRegion && branding && (
            <div>
              Conference: <span className="text-foreground font-medium">{branding.title}</span>
              {branding.theme ? ` · Theme: ${branding.theme}` : ""}
              {branding.venueName ? ` · ${branding.venueName}` : ""}
              {branding.city ? ` · ${branding.city}${branding.state ? `, ${branding.state.toUpperCase()}` : ""}` : ""}
            </div>
          )}
          {needsRegionPick && (
            <div className="text-foreground">Select a region above to load its conference details before designing or printing badges.</div>
          )}
        </CardContent>

      </Card>

      <BadgeColorLegend />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="attendees">Attendees</TabsTrigger>
          <TabsTrigger value="designer">Designer / Preview</TabsTrigger>
          <TabsTrigger value="bulk">Bulk print</TabsTrigger>
        </TabsList>

        <TabsContent value="attendees" className="mt-4">
          <AttendeesTable
            attendees={visibleAttendees}
            regions={regions}
            selectedKeys={selectedKeys}
            onSelect={setSelectedKeys}
            onOverrideType={onOverrideType}
            onPreview={(k) => { setActiveKey(k); setTab("designer"); }}
          />
        </TabsContent>

        <TabsContent value="designer" className="mt-4">
          {needsRegionPick ? (
            <Card><CardContent className="py-10 text-center text-muted-foreground">
              Pick a region to preview badges with that region's conference title, theme, dates and venue.
            </CardContent></Card>
          ) : (
            <DesignerPanel
              attendees={visibleAttendees}
              branding={branding}
              activeKey={activeKey}
              setActiveKey={setActiveKey}
              templateId={templateId}
              setTemplateId={setTemplateId}
            />
          )}
        </TabsContent>

        <TabsContent value="bulk" className="mt-4">
          {needsRegionPick ? (
            <Card><CardContent className="py-10 text-center text-muted-foreground">
              Pick a region to bulk-print badges with the correct regional conference details.
            </CardContent></Card>
          ) : (
            <BulkPanel
              attendees={visibleAttendees}
              selectedAttendees={selectedAttendees}
              branding={branding}
              templateId={templateId}
              setTemplateId={setTemplateId}
            />
          )}
        </TabsContent>

      </Tabs>
    </div>

  );
}
